/**
 * PageContextService - Manages contextual awareness for AI chatbot interactions
 * 
 * Boundary: Does not handle chat logic or AI prompts - only context tracking
 * External dependencies: None - pure in-memory state management
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Page context for chatbot awareness.
 * Tracks what page/resource the user is currently viewing to provide better AI responses.
 */
export interface PageContext {
  /**
   * Type of page/resource being viewed
   */
  pageType:
    | 'scan'
    | 'vulnerability'
    | 'asset'
    | 'dark-web'
    | 'remediation'
    | 'dashboard'
    | 'other';

  /**
   * Primary resource ID (e.g., scan ID, asset ID)
   */
  resourceId?: string;

  /**
   * Human-readable resource name
   */
  resourceName?: string;

  /**
   * Additional context data
   */
  metadata?: Record<string, any>;

  /**
   * When this context was set
   */
  timestamp: Date;
}

/**
 * Manages page context state for AI chatbot integration.
 * 
 * Dependencies:
 * - BehaviorSubject: Real-time context broadcasting
 * 
 * Side effects:
 * - BehaviorSubject: contextSubject (emits context changes)
 * 
 * @example
 * ```typescript
 * // In scan-view.component.ts
 * constructor(private pageContext: PageContextService) {}
 *
 * ngOnInit() {
 *   this.pageContext.setContext({
 *     pageType: 'scan',
 *     resourceId: this.scanId,
 *     resourceName: this.scanDetails.info.name,
 *     metadata: {
 *       status: this.scanDetails.info.status,
 *       completedAt: this.scanDetails.info.scan_end
 *     }
 *   });
 * }
 *
 * ngOnDestroy() {
 *   this.pageContext.clearContext();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class PageContextService {
  private contextSubject = new BehaviorSubject<PageContext | null>(null);

  public context$: Observable<PageContext | null> =
    this.contextSubject.asObservable();

  /**
   * Sets the current page context for chatbot awareness.
   * 
   * @param context - Context data without timestamp (auto-added)
   * @returns void
   * - Observable semantics: Emits to context$ subscribers immediately
   * - State touched: contextSubject updated
   * - Idempotency: Safe to call multiple times; replaces previous context
   */
  setContext(context: Omit<PageContext, 'timestamp'>): void {
    const fullContext: PageContext = {
      ...context,
      timestamp: new Date(),
    };
    this.contextSubject.next(fullContext);
  }

  /**
   * Returns current page context snapshot.
   * 
   * @returns Current context or null if not set
   * - Observable semantics: Synchronous snapshot; does not emit
   * - State touched: None (read-only)
   * - Idempotency: Safe to call repeatedly
   */
  getContext(): PageContext | null {
    return this.contextSubject.value;
  }

  /**
   * Clears the current page context.
   * 
   * @returns void
   * - Observable semantics: Emits null to context$ subscribers
   * - State touched: contextSubject set to null
   * - Idempotency: Safe to call multiple times
   */
  clearContext(): void {
    this.contextSubject.next(null);
  }

  /**
   * Updates specific fields in the current context without full replacement.
   * 
   * @param updates - Partial context updates (timestamp auto-updated)
   * @returns void
   * - Observable semantics: Emits updated context to subscribers if current context exists
   * - State touched: contextSubject merged with updates
   * - Idempotency: Safe to call multiple times; does nothing if no current context
   */
  updateContext(updates: Partial<Omit<PageContext, 'timestamp'>>): void {
    const current = this.contextSubject.value;
    if (current) {
      this.setContext({
        ...current,
        ...updates,
      });
    }
  }
}
